import DateInput from '../../components/DateInput';
/**
 * FinanceExpenses — "Payroll & expenses" tab inside Finances (iter 145).
 * Categorized expense entries (salary, training, uniform, …) with staff
 * attribution + a tax-ready statement (totals by category / staff / month).
 * Executed HR payroll runs land here automatically as "salary".
 */
import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { safeConfirm } from '../../lib/confirm';
import { Plus, Trash2, Banknote, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export const FinanceExpenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [staff, setStaff] = useState([]);
  const [statement, setStatement] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [summaryMonth, setSummaryMonth] = useState(new Date().toISOString().slice(0, 7));
  const [summarySending, setSummarySending] = useState(false);
  const [form, setForm] = useState({
    category: 'salary', staff_employee_id: '', staff_name: '',
    amount: '', currency: 'PLN', date: new Date().toISOString().slice(0, 10),
    description: '', payment_reference: '',
  });

  const emailSummary = async () => {
    setSummarySending(true);
    try {
      const { data } = await api.post('/finance/payroll-summary/email', { month: summaryMonth });
      if (data.sent) toast.success(`Payroll summary for ${summaryMonth} sent to ${data.to} — ${data.runs} run(s), ${data.total_net.toFixed(2)} net total.`);
      else toast.warning(`Summary computed (${data.runs} runs, ${data.total_net.toFixed(2)} net) but email is not configured.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Summary failed.');
    } finally { setSummarySending(false); }
  };

  const load = () => {
    api.get('/finance/expenses', { params: { year } }).then(r => setExpenses(r.data || []));
    api.get('/finance/expenses/statement', { params: { year } }).then(r => setStatement(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [year]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get('/finance/expense-categories').then(r => setCategories(r.data?.categories || []));
    api.get('/hr/employees').then(r => setStaff(r.data || [])).catch(() => {});
  }, []);

  const save = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Enter a positive amount.'); return; }
    setSaving(true);
    try {
      const emp = staff.find(s => s.employee_id === form.staff_employee_id);
      await api.post('/finance/expenses', {
        ...form,
        amount: parseFloat(form.amount),
        staff_employee_id: form.staff_employee_id || null,
        staff_name: emp ? `${emp.first_name} ${emp.last_name}` : (form.staff_name || null),
        description: form.description || null,
        payment_reference: form.payment_reference || null,
      });
      toast.success('Expense recorded.');
      setShowForm(false);
      setForm(f => ({ ...f, amount: '', description: '', payment_reference: '' }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!(await safeConfirm('Delete this expense entry?'))) return;
    try {
      await api.delete(`/finance/expenses/${id}`);
      load();
      toast.success('Deleted.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  return (
    <div className="space-y-5" data-testid="finance-expenses-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Banknote className="w-5 h-5 text-brand"/>
          <h2 className="font-heading font-semibold text-lg">Payroll & expense statement</h2>
          <select className="input !w-28" value={year} onChange={e => setYear(parseInt(e.target.value, 10))} data-testid="fe-year">
            {[0, 1, 2].map(d => { const y = new Date().getFullYear() - d; return <option key={y} value={y}>{y}</option>; })}
          </select>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="month" className="input !w-40" value={summaryMonth} onChange={e => setSummaryMonth(e.target.value)} data-testid="fe-summary-month"/>
          <button className="btn-secondary text-sm inline-flex items-center gap-2" onClick={emailSummary} disabled={summarySending} data-testid="fe-summary-btn">
            {summarySending && <Loader2 className="w-3.5 h-3.5 animate-spin"/>} Email payroll summary
          </button>
          <button className="btn-primary text-sm inline-flex items-center gap-2" onClick={() => setShowForm(s => !s)} data-testid="fe-new-btn">
            <Plus className="w-4 h-4"/> {showForm ? 'Close' : 'New expense'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bento-card" data-testid="fe-form">
          <div className="grid md:grid-cols-3 gap-3">
            <label className="block"><div className="text-xs text-fg-muted mb-1">Category</div>
              <select className="input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} data-testid="fe-category">
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block"><div className="text-xs text-fg-muted mb-1">Staff member (optional)</div>
              <select className="input" value={form.staff_employee_id} onChange={e => setForm(f => ({ ...f, staff_employee_id: e.target.value }))} data-testid="fe-staff">
                <option value="">— Not staff-related —</option>
                {staff.map(s => <option key={s.employee_id} value={s.employee_id}>{s.first_name} {s.last_name}</option>)}
              </select>
            </label>
            <label className="block"><div className="text-xs text-fg-muted mb-1">Date</div>
              <DateInput value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} data-testid="fe-date"/>
            </label>
            <label className="block"><div className="text-xs text-fg-muted mb-1">Amount</div>
              <input type="number" min="0" step="0.01" className="input" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} data-testid="fe-amount"/>
            </label>
            <label className="block"><div className="text-xs text-fg-muted mb-1">Currency</div>
              <select className="input" value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>
                <option>PLN</option><option>EUR</option><option>USD</option><option>GBP</option><option>CAD</option>
              </select>
            </label>
            <label className="block"><div className="text-xs text-fg-muted mb-1">Payment reference</div>
              <input className="input" value={form.payment_reference} onChange={e => setForm(f => ({ ...f, payment_reference: e.target.value }))} placeholder="e.g. Salary June 2026"/>
            </label>
          </div>
          <label className="block mt-3"><div className="text-xs text-fg-muted mb-1">Description</div>
            <input className="input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g. Manual therapy course — Kraków" data-testid="fe-description"/>
          </label>
          <div className="flex justify-end mt-3">
            <button className="btn-primary text-sm inline-flex items-center gap-2" disabled={saving} onClick={save} data-testid="fe-save-btn">
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin"/>} Save expense
            </button>
          </div>
        </div>
      )}

      {statement && (
        <div className="grid md:grid-cols-3 gap-4" data-testid="fe-statement">
          <div className="bento-card">
            <div className="text-xs uppercase tracking-wider text-fg-muted mb-2">By category — {statement.year}</div>
            {Object.keys(statement.by_category).length === 0
              ? <div className="text-xs text-fg-muted">No entries yet.</div>
              : Object.entries(statement.by_category).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm py-0.5"><span className="capitalize">{k}</span><span className="font-medium">{v.toFixed(2)}</span></div>
              ))}
            <div className="flex justify-between text-sm font-bold border-t border-soft mt-1 pt-1"><span>Total</span><span data-testid="fe-total">{statement.total.toFixed(2)}</span></div>
          </div>
          <div className="bento-card">
            <div className="text-xs uppercase tracking-wider text-fg-muted mb-2">By staff member</div>
            {Object.keys(statement.by_staff).length === 0
              ? <div className="text-xs text-fg-muted">No staff-attributed entries.</div>
              : Object.entries(statement.by_staff).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm py-0.5"><span>{k}</span><span className="font-medium">{v.toFixed(2)}</span></div>
              ))}
          </div>
          <div className="bento-card">
            <div className="text-xs uppercase tracking-wider text-fg-muted mb-2">By month</div>
            {Object.keys(statement.by_month).length === 0
              ? <div className="text-xs text-fg-muted">No entries yet.</div>
              : Object.entries(statement.by_month).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm py-0.5"><span>{k}</span><span className="font-medium">{v.toFixed(2)}</span></div>
              ))}
          </div>
        </div>
      )}

      <div className="bento-card overflow-x-auto" data-testid="fe-list">
        {expenses.length === 0 ? (
          <div className="text-center py-8 text-sm text-fg-muted">No payroll or expense entries for {year} yet. Executed HR payroll runs appear here automatically.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-fg-muted text-xs uppercase tracking-wider border-b border-soft">
                <th className="text-left py-2 px-2">Date</th>
                <th className="text-left py-2 px-2">Category</th>
                <th className="text-left py-2 px-2">Staff</th>
                <th className="text-left py-2 px-2">Description</th>
                <th className="text-left py-2 px-2">Reference</th>
                <th className="text-right py-2 px-2">Amount</th>
                <th className="text-right py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {expenses.map(x => (
                <tr key={x.expense_id} className="border-b border-soft/50 hover:bg-bg-secondary/30" data-testid={`fe-row-${x.expense_id}`}>
                  <td className="py-2.5 px-2">{x.date}</td>
                  <td className="py-2.5 px-2"><span className="badge-neutral capitalize text-[10px]">{x.category}</span>{x.source === 'payroll_run' && <span className="text-[9px] text-fg-muted ml-1">(payroll)</span>}</td>
                  <td className="py-2.5 px-2">{x.staff_name || '—'}</td>
                  <td className="py-2.5 px-2 text-xs text-fg-muted">{x.description || '—'}</td>
                  <td className="py-2.5 px-2 text-xs text-fg-muted">{x.payment_reference || '—'}</td>
                  <td className="py-2.5 px-2 text-right font-medium">{Number(x.amount).toFixed(2)} {x.currency}</td>
                  <td className="py-2.5 px-2 text-right">
                    <button onClick={() => del(x.expense_id)} className="text-xs text-[#B83A3A] hover:opacity-70" data-testid={`fe-delete-${x.expense_id}`}>
                      <Trash2 className="w-3 h-3"/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
